import { type PaginationTypes } from '@shared/hooks'
import { usePaginationConfigCrm } from '@shared/hooks/usePaginationConfigCrm'
import { enhanceColumns } from '@shared/utils'
import { Flex, Space, Table } from 'antd'
import { type ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useStyles } from './styles'
import { SearchInput } from '../Inputs'

import type { ColumnsType } from 'antd/es/table'
import type { TableProps } from 'antd/lib'

type Props<T> = {
  data?: PaginationTypes<T[]>
  columns: ColumnsType<T>
  isLoading?: boolean
  leftChildren?: ReactNode
  rightChildren?: ReactNode
  showSearch?: boolean
  searchPlaceholder?: string
  isHoverAlternative?: boolean
  children?: ReactNode
} & TableProps<T>

export const TableContainer = <T,>({
  data,
  columns,
  isLoading = false,
  leftChildren,
  rightChildren,
  showSearch = true,
  searchPlaceholder,
  children,
  isHoverAlternative,
  ...props
}: Props<T>) => {
  const { styles } = useStyles({
    onRow: !!props?.onRow,
    isHoverAlternative: !!isHoverAlternative
  })
  const { t } = useTranslation()
  const hasChildren = Boolean(rightChildren) || Boolean(leftChildren)
  const hasTitleContent = showSearch || hasChildren

  const titleContent = hasTitleContent ? (
    <Flex gap={8} align="start" justify="space-between">
      <Flex align="start" wrap gap={8}>
        {showSearch && (
          <Space>
            <SearchInput
              enterButton={false}
              placeholder={searchPlaceholder ?? t('search')}
            />
          </Space>
        )}

        {leftChildren}
      </Flex>

      {rightChildren && (
        <Flex justify="flex-end" wrap>
          {rightChildren}
        </Flex>
      )}
    </Flex>
  ) : undefined

  const enhancedColumns = useMemo(() => enhanceColumns(columns), [columns])

  const defaultPagination = usePaginationConfigCrm(data)
  const pagination = props?.pagination ?? defaultPagination

  return (
    <Flex vertical>
      <Table<T>
        {...(hasTitleContent && { title: () => titleContent })}
        rowKey="id"
        loading={isLoading}
        dataSource={data?.results ?? []}
        columns={enhancedColumns}
        scroll={{ x: 'max-content' }}
        pagination={pagination}
        onRow={props?.onRow}
        className={styles.table}
        {...props}
      />
      {children}
    </Flex>
  )
}
